import { tags } from '@/cms/api/tags';
import { cmsApiEndpoint, env, nextApiEndpoint } from '@/constant/env';
import { withStrapiHeaders } from '@/utils/fetch';

import { Logger } from './Logging';

export interface RedirectEntriesProps {
  from: string;
  to: string;
  redirectStatusCode: 'permanent-301' | 'temporary-302';
}

export interface RedirectsProps {
  redirects: RedirectEntriesProps[];
}

export const getRedirectsFromStrapi = async () => {
  const logger = new Logger('getRedirectsFromStrapi');
  const URL = `${cmsApiEndpoint}/redirect?populate=redirects&locale=${env.publicLocale}`;
  try {
    const res = await fetch(URL, {
      headers: withStrapiHeaders({
        'Strapi-Response-Format': 'v5',
      }),
      next: { revalidate: false, tags: [tags.redirects()] },
      cache: 'force-cache',
    }).catch((error) => {
      logger.error({
        context: {
          error: error.message,
          URL,
        },
        message: '[REDIRECT] ❌ Error fetching redirects from Strapi',
      });
      return error;
    });

    if (!res.ok) {
      logger.error({
        message: `Strapi returned error status when getting redirects: ${res.status}`,
        context: {
          status: res.status,
          statusText: res.statusText,
          headers: res.headers,
          URL,
        },
      });
      return [];
    }

    const data = await res.json();

    return (data.data?.redirects ?? []).map((entry: any) => ({
      from: entry.from || '',
      to: entry.to || '',
      redirectStatusCode:
        entry.redirectStatusCode === 'permanent-301' ||
        entry.redirectStatusCode === 'temporary-302'
          ? entry.redirectStatusCode
          : 'permanent-301',
    }));
  } catch (error) {
    logger.error({
      context: {
        error: error instanceof Error ? error.message : String(error),
        URL,
      },
      message: '[REDIRECT] ❌ Exception fetching redirects from Strapi',
    });
    return [];
  }
};

export const getRedirectsFromNextApi = async () => {
  const logger = new Logger('getRedirectsFromNextApi');

  const URL = `${nextApiEndpoint}/redirect`;
  try {
    const res = await fetch(URL);

    const data = await res.json();
    if (Array.isArray(data) && data?.length) {
      return data;
    }
    logger.log({
      message: `⚠️ NextApi returned non-array or empty data.`,
      context: {
        data,
        URL,
      },
    });
    return [];
  } catch (error) {
    logger.error({
      message:
        '[REDIRECT] ❌ Error fetching redirects from NextApi - returning empty array',
      context: {
        error: error instanceof Error ? error.message : String(error),
        nextApiEndpoint,
        locale: env.publicLocale,
        URL,
      },
    });

    return [];
  }
};
